import { AddBusinessOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Grid, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { UserRequest } from '../models/userRequest';
import { UserRenderInvoiceGroupsList } from './userRenderInvoiceGroupsList';

type UserEditInvoiceGroupSummaryProps = {
  initialInvoiceGroup: InvoiceGroup[] | [];
};

export const UserEditInvoiceGroupSummary = (props: UserEditInvoiceGroupSummaryProps) => {
  const { initialInvoiceGroup } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation(['user']);
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[] | []>([]);

  const { setValue } = useFormContext();
  const {
    fields: invoiceGroupFields,
    append: appendInvoiceGroup,
    remove: removeInvoiceGroup,
  } = useFieldArray<UserRequest, 'invoiceGroups', 'id'>({
    name: 'invoiceGroups',
  });
  const [showInvoiceGroups, setShowInvoiceGroups] = useState<boolean>(invoiceGroupFields.length > 0);

  const areInvoiceGroupsEqual =
    JSON.stringify(invoiceGroupFields.map((item) => item.owner_id)) === JSON.stringify(initialInvoiceGroup?.map((item) => item.owner_id));

  const removeMyInvoice = (ownerId: number) => {
    if (invoiceGroupFields) {
      const removeIndex = invoiceGroupFields.findIndex((item) => item.owner_id === ownerId);

      if (removeIndex !== -1) {
        removeInvoiceGroup(removeIndex);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllInvoiceGroups(0, 500, 'name', 'asc', '');
        if (isMounted()) {
          setInvoiceGroups(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };

    fetchData();
  }, [isMounted]);

  const filteredInvoiceGroups = useMemo(() => {
    return invoiceGroups.filter((item) => !invoiceGroupFields?.some((myItem) => myItem.owner_id === item.owner_id));
  }, [invoiceGroupFields, invoiceGroups]);

  return (
    <Alert
      severity='info'
      icon={<AddBusinessOutlined />}
      sx={{
        border: (thm) => `1px solid ${thm.palette.divider}`,
        '& .MuiAlert-message': { width: '100% !important' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: 2 }}>
        <AlertTitle sx={{ fontWeight: 600 }}>{t('user:invoiceGroup.invoiceGroups')}</AlertTitle>
      </Box>
      {!showInvoiceGroups && (
        <Grid container sx={{ width: '100%' }}>
          <Grid item xs={12} mb={1}>
            <Typography gutterBottom sx={{ fontStyle: 'italic' }}>
              {t('user:invoiceGroup.noInvoiceGroups')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button variant='outlined' size='small' onClick={() => setShowInvoiceGroups(true)}>
              {t('common:button.add')}
            </Button>
          </Grid>
        </Grid>
      )}
      {showInvoiceGroups && (
        <Grid container spacing={3}>
          <Grid item sm={12} lg={6}>
            <Typography gutterBottom>
              <Box component='span' sx={{ fontWeight: 500 }}>
                {invoiceGroups?.length > 1 ? t('user:invoiceGroup.selectInvoiceGroups') : t('user:invoiceGroup.selectInvoiceGroup')}
              </Box>
              <Box component='small' sx={{ fontWeight: 400 }}>
                ({Array.isArray(filteredInvoiceGroups) ? filteredInvoiceGroups.length : 0})
              </Box>
            </Typography>
            {filteredInvoiceGroups.length > 0 && (
              <UserRenderInvoiceGroupsList invoiceGroupList={filteredInvoiceGroups} onAdd={(item) => appendInvoiceGroup(item)} />
            )}
          </Grid>

          <Grid item sm={12} lg={6}>
            <Typography gutterBottom>
              <Box component='span' sx={{ fontWeight: 500 }}>
                {t('user:invoiceGroup.added')}
              </Box>
              <Box component='small' sx={{ fontWeight: 400 }}>
                ({Array.isArray(invoiceGroupFields) ? invoiceGroupFields.length : 0})
              </Box>
            </Typography>
            {invoiceGroupFields != null && invoiceGroupFields?.length > 0 && (
              <UserRenderInvoiceGroupsList invoiceGroupList={invoiceGroupFields} onRemove={(id) => removeMyInvoice(id)} />
            )}
          </Grid>

          {!areInvoiceGroupsEqual && (
            <Grid item xs={12} mt={2}>
              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  if (initialInvoiceGroup !== null) {
                    setValue('invoiceGroups', initialInvoiceGroup);
                  } else {
                    removeInvoiceGroup();
                    setShowInvoiceGroups(false);
                  }
                }}
              >
                {initialInvoiceGroup.length > 0 ? t('user:restore') : t('common:button.cancel')}
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Alert>
  );
};
